import { useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ErrorAlert, ErrorServer, SuccessAlert } from "@shared/ui/alerts";
import { InputController } from "@shared/ui/form";
import { useProjectsWithAssignmentsJira } from "../hooks/useProjectsWithAssignmentsJira";
import { useProjectIssuesJira } from "../hooks/useProjectIssuesJira";
import { useChargeHoursJira } from "../hooks/useChargeHoursJira";
import { buildChargeHourJiraSchema } from "../schema/chargeHourJiraSchema";
import { SelectProjectRegisterHour } from "./SelectProjectRegisterHour";
import { EntitySelect } from "./EntitySelect";

import type { ChargeHourJiraValues } from "../schema/chargeHourJiraSchema";

import type { TFunction } from "i18next";

export interface ChargeHourJiraDialogProps {
  handleClose: () => void;
  open: boolean;
  notLoading: () => void;
  loading: () => void;
  reloadInitialMissingHour: () => void;
  t: TFunction;
}

export const ChargeHourJiraDialog = ({
  handleClose,
  open,
  notLoading,
  loading,
  reloadInitialMissingHour,
  t,
}: ChargeHourJiraDialogProps) => {
  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<ChargeHourJiraValues>({ resolver: yupResolver(buildChargeHourJiraSchema(t)) as never });

  const projectId = watch("projectId");

  const { data: projectsData, error: projectsError } = useProjectsWithAssignmentsJira(open);
  const listProject = projectsData ?? [];
  const staffing = projectsData?.filter((item) => item.isStaffing) ?? [];
  const projects = projectsData?.filter((item) => !item.isStaffing) ?? [];

  useEffect(() => {
    if (projectsError) ErrorServer().then();
  }, [projectsError]);

  const { data: issues } = useProjectIssuesJira(projectId);

  const onClose = () => {
    handleClose();
    // `hours` no es un campo de este form (el real es `hoursPerDay`): typo preexistente y la
    // clave sobra. No rompe nada: `reset` reemplaza todos los valores, `hoursPerDay` queda
    // sin valor y al reabrir el dialog el `InputController` le vuelve a aplicar
    // `defaultValue={0}`. Se deja tal cual para no cambiar comportamiento.
    // @ts-expect-error -- ver comentario arriba: "hours" no existe en ChargeHourJiraValues
    reset({ projectId: "", issueId: "", hours: 0 });
  };

  const chargeHoursJira = useChargeHoursJira();

  const onRegister = (values: ChargeHourJiraValues) => {
    onClose();
    loading();
    chargeHoursJira.mutate(values, {
      onSuccess: () => {
        notLoading();
        SuccessAlert(t("loadHoursInJira"), t("doneSuccessfully", { a: "a" })).then(() =>
          reloadInitialMissingHour()
        );
      },
      onError: (error) => {
        notLoading();
        ErrorAlert(t("errorTitle"), error.response?.data?.message ?? t("internalError")).then();
      },
      onSettled: () => onClose(),
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onRegister)}>
        <DialogTitle>{t("loadHoursInJira")}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <SelectProjectRegisterHour
                  control={control}
                  defaultValue=""
                  label={t("project")}
                  name="projectId"
                  listValue={listProject}
                  listStaffing={staffing}
                  listProjects={projects}
                />
                {errors.projectId && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.projectId.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <EntitySelect
                  control={control}
                  defaultValue=""
                  label={t("issue")}
                  name="issueId"
                  listValue={issues}
                  labelKey="name"
                />
                {errors.issueId && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.issueId.message}</span>
                )}
              </FormControl>
            </Grid>
            <Grid id="hours-field" size={12}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputController
                  control={control}
                  defaultValue={0}
                  label={t("hours")}
                  name="hoursPerDay"
                />
                {errors.hoursPerDay && (
                  <span style={{ fontSize: 12, color: "red" }}>{errors.hoursPerDay.message}</span>
                )}
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t("cancel")}</Button>
          <Button type="submit">{t("load")}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
